import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "../../core/translation.core";
import ProductSearchInput from "../product/productSearchInput.core";
import { EditorSection, Title } from "../reuseables/editor.comp";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { inputCss, inputCssNoMy } from "../reuseables/native.comp";
import DeleteIcon from "../../icons/delete.icon";
import { LabelAndInput } from "../reuseables/form.comp";
import PlusIcon from "../../icons/plus.icon";
import { AdocFormData2, ManualOrderFormData } from "../../types/order";

interface Product {
  productId: string;
  name: string;
  quantity: number;
  price: number;
  inventory: number;
  discount: number | null;
  productType?: string;
}

export default function ManualOrderProducts({
  methods,
  selectedProducts,
  setSelectedProducts,
}: {
  methods: UseFormReturn<ManualOrderFormData, any>;
  selectedProducts: Product[];
  setSelectedProducts: Dispatch<SetStateAction<Product[]>>;
}) {
  const { t, msg } = useTranslation();

  const [orderTotal, setOrderTotal] = useState(0);
  const [adoctotal, setAdocTotal] = useState(0);

  useEffect(() => {
    const subscription = methods.watch((value) => {
      const totalValue = value.adocItems?.reduce((sum, item) => sum + (item?.price || 0) * (item?.quantity || 0), 0) || 0;
      setAdocTotal(totalValue);
    });

    return () => subscription.unsubscribe();
  }, [methods, methods.watch]);

  function addProduct(
    productId: string,
    productDetails: { name: string; price: number; inventory: number; productType: string }
  ) {
    setSelectedProducts((prev) => {
      const existingProductIndex = prev.findIndex((p) => p.productId === productId);

      if (existingProductIndex >= 0) {
        const updated = [...prev];
        updated[existingProductIndex].quantity += 1;
        calculateTotal(updated);
        return updated;
      } else {
        const updated = [
          ...prev,
          {
            productId,
            name: productDetails.name,
            quantity: 1,
            price: productDetails.price || 0,
            discount: null,
            inventory: productDetails.inventory || 0,
            productType: productDetails.productType,
          },
        ];
        calculateTotal(updated);
        return updated;
      }
    });
  }

  function updateDiscount(index: number, discount: string) {
    setSelectedProducts((prev) => {
      const updated = [...prev];
      updated[index].discount = discount === "" ? null : parseFloat(discount);
      calculateTotal(updated);
      return updated;
    });
  }

  function removeProduct(index: number) {
    setSelectedProducts((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      calculateTotal(updated);
      return updated;
    });
  }

  function updateQuantity(index: number, newQuantity: string) {
    setSelectedProducts((prev) => {
      const updated = [...prev];
      updated[index].quantity = parseInt(newQuantity) || 0;
      calculateTotal(updated);
      return updated;
    });
  }

  function calculateTotal(products: Product[]) {
    const subtotal = products.reduce((sum, item) => sum + (item.price * item.quantity - (item.discount || 0)), 0);

    const shipping = methods.getValues("shipping") || 0;
    // const tax = parseFloat(methods.getValues("taxTotal") as any) || 0;
    const total = subtotal + shipping;
    setOrderTotal(total);
    methods.setValue("total", total);
    methods.setValue("itemsTotal", subtotal);
  }

  return (
    <EditorSection>
      <Title>{t(msg.product.product)}</Title>
      <div className="flex flex-col gap-4">
        <ProductSearchInput addProduct={addProduct} showVariants showParent={false} withInventories={true} />

        {selectedProducts.map((product, index) => (
          <div key={`${product.productId}-${index}`} className="border rounded-lg p-4">
            <div id="selected-products" className="flex gap-4 items-center">
              <div className="flex-1">
                <p className="font-medium  mb-2">{product.name}</p>
                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t(msg.order.quantity)}</label>
                    <input
                      id="product-quantity"
                      type="number"
                      min="1"
                      value={product.quantity}
                      onChange={(e) => updateQuantity(index, e.target.value)}
                      className={`w-24 p-2 border rounded ${inputCss}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t(msg.order.unitprice)}</label>
                    <p className="p-2">{product.price.toFixed(2)}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t(msg.order.discount)}</label>
                    <input
                      id="product-discount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t(msg.order.discount)}
                      value={product.discount ?? ""}
                      onChange={(e) => updateDiscount(index, e.target.value)}
                      className={`w-24 p-2 border rounded ${inputCss}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t(msg.order.subTotal)}</label>
                    <div className="p-2">
                      {product.discount ? (
                        <div className="flex flex-col">
                          <span className="text-gray-500 line-through">{(product.price * product.quantity).toFixed(2)}</span>
                          <span className="font-medium">{(product.price * product.quantity - product.discount).toFixed(2)}</span>
                        </div>
                      ) : (
                        <p>{(product.price * product.quantity).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t(msg.inventory.inventory)}</label>
                    <div className="p-2">
                      {/* {product.discount ? ( */}
                      <div className="flex flex-col">
                        {/* <span className="text-gray-500 line-through">{t(msg.inventory.instock)}</span> */}
                        <span className="font-medium">{product.inventory ? product.inventory : 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeProduct(index)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg self-center"
              >
                <DeleteIcon className="w-4 h-4" />
              </button>
            </div>
            {product.inventory && product.inventory < product.quantity ? (
              <span className="text-red-500 text-xs mt-1">{t(msg.inventory.quantityLess)}</span>
            ) : null}
          </div>
        ))}
      </div>
      <Title>{t(msg.adhocItem.adhoc)}</Title>
      <AdocItemEditor methods={methods} />
      <Title>{""}</Title>
      <div className="flex flex-col gap-4 items-end">
        {selectedProducts.length > 0 && (
          <div className="flex justify-end mt-4">
            <div className="text-right">
              <p className="text-sm">{t(msg.order.orderTotal)}:</p>
              {selectedProducts.some((p) => p.discount) ? (
                <div>
                  <p className="text-gray-500 line-through">
                    {selectedProducts.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}
                  </p>
                  <p className="text-xl font-semibold">{(orderTotal + adoctotal).toFixed(2)}</p>
                </div>
              ) : (
                <p className="text-xl font-semibold">{(orderTotal + adoctotal).toFixed(2)}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </EditorSection>
  );
}

const AdocItemEditor = ({ methods }: { methods: UseFormReturn<ManualOrderFormData, any> }) => {
  const { t, msg } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "adocItems",
  });

  return (
    <div id="adoc-container">
      <button
        id="add-adoc"
        type="button"
        onClick={() => append({ description: "", quantity: 0, price: 0 })}
        className="flex space-x-2 mt-2  px-3 py-2 rounded-md items-center"
      >
        <span className="underline cursor-pointer">Add Adoc Items</span>
        <PlusIcon className="w-4 h-4" />
      </button>
      {fields.map((item, index) => (
        <div id="adoc-sub-container" key={item.id} className="flex flex-row justify-between items-center space-x-2 mb-2">
          <div className="flex flex-1">
            <LabelAndInput
              type="text"
              register={methods.register}
              fieldName={`adocItems.${index}.description`}
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              label={t(msg.adhocItem.description)}
              errors={methods.formState.errors}
            />
          </div>
          <div className="flex flex-1">
            <LabelAndInput
              type="number"
              register={methods.register}
              fieldName={`adocItems.${index}.price`}
              topLabelCss="w-full text-md "
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.adhocItem.price)}
              errors={methods.formState.errors}
            />
          </div>
          <div className="flex flex-1">
            <LabelAndInput
              type="number"
              register={methods.register}
              fieldName={`adocItems.${index}.quantity`}
              topLabelCss="w-full"
              labelCss="-mb-[.3px]"
              registerOptions={{ min: 0 }}
              label={t(msg.adhocItem.quantity)}
              errors={methods.formState.errors}
            />
          </div>
          <div className="flex  items-center space-x-2">
            {index >= 0 ? (
              <button
                type="button"
                onClick={() => remove(index)}
                className="px-3 py-2 items-center mt-2 cursor-pointer underline text-red-600"
              >
                <DeleteIcon className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};
